import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Tooltip, Tag } from "antd";
import { updatePipelineStageThunk } from "../../../Slices/salesSlice";

const PIPELINE_STAGES = [
  { key: "Prospect", color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  { key: "Qualified", color: "#854d0e", bg: "#fef9c3", border: "#fde68a" },
  { key: "Proposal", color: "#c2410c", bg: "#fde8d8", border: "#fdba74" },
  { key: "Negotiation", color: "#6d28d9", bg: "#ede9fe", border: "#c4b5fd" },
  { key: "Onboarding", color: "#0e7490", bg: "#cffafe", border: "#67e8f9" },
  { key: "Closed Won", color: "#15803d", bg: "#dcfce7", border: "#86efac" },
  { key: "Closed Lost", color: "#b91c1c", bg: "#fee2e2", border: "#fca5a5" },
];

const ITEM_TYPE = "LEAD_CARD";

const LeadCard = ({ lead, onNavigate }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: lead.id, currentStage: lead.pipelineStage },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const daysInStage = lead.lastUpdatedDate
    ? Math.floor((Date.now() - parseInt(lead.lastUpdatedDate)) / 86400000)
    : null;

  return (
    <div
      ref={drag}
      onClick={() => onNavigate(lead.id)}
      style={{
        opacity: isDragging ? 0.4 : 1,
        background: "#fff",
        borderRadius: 8,
        padding: "10px 12px",
        marginBottom: 8,
        cursor: "grab",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)")}
    >
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, color: "#1f2937" }}>
        {lead.agencyName || "Unnamed"}
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
        {[lead.city, lead.state].filter(Boolean).join(", ")}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
        {lead.dealValue && (
          <span style={{ fontWeight: 700, fontSize: 13, color: "#059669" }}>
            ${lead.dealValue}
          </span>
        )}
        {lead.assignedTo && (
          <Tooltip title={lead.assignedTo}>
            <span style={{
              fontSize: 10, background: "#f3f4f6", padding: "1px 6px",
              borderRadius: 8, color: "#374151", maxWidth: 90, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block",
            }}>
              {lead.assignedTo}
            </span>
          </Tooltip>
        )}
      </div>
      {lead.priority && (
        <Tag
          color={lead.priority === "High" ? "red" : lead.priority === "Medium" ? "gold" : "green"}
          style={{ fontSize: 10, marginTop: 4, borderRadius: 8 }}
        >
          {lead.priority}
        </Tag>
      )}
      {daysInStage !== null && daysInStage > 7 && (
        <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>
          {daysInStage}d in stage
        </div>
      )}
    </div>
  );
};

const StageColumn = ({ stage, leads, onDrop, onNavigate }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item) => {
      if (item.currentStage !== stage.key) {
        onDrop(item.id, stage.key);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  const totalDealValue = leads.reduce((sum, l) => sum + (parseFloat(l.dealValue) || 0), 0);

  return (
    <div
      ref={drop}
      style={{
        flex: "1 1 0",
        minWidth: 200,
        maxWidth: 280,
        background: isOver ? stage.bg : "#f9fafb",
        border: `1px solid ${isOver ? stage.border : "#e5e7eb"}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      {/* Column Header */}
      <div style={{
        padding: "10px 12px 8px",
        borderBottom: `2px solid ${stage.border}`,
        background: stage.bg,
        borderRadius: "10px 10px 0 0",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: stage.color }}>
            {stage.key}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, background: "#fff",
            padding: "1px 8px", borderRadius: 10, color: stage.color,
          }}>
            {leads.length}
          </span>
        </div>
        {totalDealValue > 0 && (
          <div style={{ fontSize: 11, color: stage.color, marginTop: 2, fontWeight: 600 }}>
            ${totalDealValue.toLocaleString()}
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={{
        padding: 8,
        flex: 1,
        overflowY: "auto",
        maxHeight: "calc(100vh - 280px)",
      }}>
        {leads.length === 0 && (
          <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: 20 }}>
            Drop leads here
          </div>
        )}
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
};

const PipelineKanban = ({ leads }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDrop = useCallback((leadId, newStage) => {
    dispatch(updatePipelineStageThunk({ leadId, pipelineStage: newStage }));
  }, [dispatch]);

  const handleNavigate = useCallback((id) => {
    navigate(`/sales/${id}`, { state: { from: "pipeline" } });
  }, [navigate]);

  // Group leads by pipeline stage
  const grouped = {};
  PIPELINE_STAGES.forEach((s) => { grouped[s.key] = []; });
  (leads || []).forEach((lead) => {
    const stage = lead.pipelineStage || "Prospect";
    if (grouped[stage]) {
      grouped[stage].push(lead);
    } else {
      grouped["Prospect"].push(lead);
    }
  });

  // Summary stats
  const totalDeals = leads?.length || 0;
  const totalValue = (leads || []).reduce((s, l) => s + (parseFloat(l.dealValue) || 0), 0);
  const wonValue = (grouped["Closed Won"] || []).reduce((s, l) => s + (parseFloat(l.dealValue) || 0), 0);

  return (
    <DndProvider backend={HTML5Backend}>
      {/* Pipeline summary */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "#374151" }}>
          <b>{totalDeals}</b> deals
        </div>
        <div style={{ fontSize: 13, color: "#059669" }}>
          Total: <b>${totalValue.toLocaleString()}</b>
        </div>
        <div style={{ fontSize: 13, color: "#15803d" }}>
          Won: <b>${wonValue.toLocaleString()}</b>
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{
        display: "flex",
        gap: 10,
        overflowX: "auto",
        paddingBottom: 10,
      }}>
        {PIPELINE_STAGES.map((stage) => (
          <StageColumn
            key={stage.key}
            stage={stage}
            leads={grouped[stage.key]}
            onDrop={handleDrop}
            onNavigate={handleNavigate}
          />
        ))}
      </div>
    </DndProvider>
  );
};

export default PipelineKanban;
